import { useCheckboxGroup } from '@chakra-ui/react'
import { mdiAccountGroup, mdiInformationOutline } from '@mdi/js'
import Icon from '@mdi/react'
import { FormEvent, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { AccordionItem, AccordionItemContent, AccordionItemTrigger, AccordionRoot } from '@/components/ui/accordion'
import { Box } from '@/components/ui/box'
import { Checkbox } from '@/components/ui/checkbox'
import { Flex } from '@/components/ui/flex'
import { Tooltip } from '@/components/ui/tooltip'
import { Text } from '@/components/ui/typography'
import useColorModeValue from '@/hooks/useColorModeValue'
import { updatesSailPointCertSelectedUsers } from '@/store/tasksApprovals/detailsDrawer'

const SailpointCertGroup = ({
    title,
    description,
    members,
    checkAll,
}: {
    title: string
    description: string
    members: {
        description: string
        user: string
        manager: string
        metadata?: {
            agreed: 'yes' | 'no'
        }
        entityId: string
        isDisabled: boolean
    }[]
    checkAll: boolean
}) => {
    const { t } = useTranslation()
    const isMounted = useRef(false)
    const [allSelected, selectAll] = useState(false)
    const disabledColor = useColorModeValue('rgba(84, 79, 64, 0.4)', 'rgba(255, 255, 255, 0.4)')
    const { value, setValue, getItemProps, toggleValue } = useCheckboxGroup({
        defaultValue: allSelected || checkAll ? members.filter((member) => !member.isDisabled).map((member) => member.entityId) : [],
    })

    // const isIndeterminate = value.some(Boolean) && !allSelected
    const isIndeterminate =
        members.filter((member) => !member.isDisabled).length > value.length &&
        members.filter((member) => !member.isDisabled).length > 1 &&
        value.some(Boolean) &&
        !allSelected
    // Group checkbox
    const onChange = () => {
        if (isIndeterminate || !allSelected) {
            setValue([...members.filter((member) => !member.isDisabled).map((member) => member.entityId)])
            updatesSailPointCertSelectedUsers([...members.filter((member) => !member.isDisabled).map((member) => member.entityId)], 'add')
            selectAll(true)
        } else {
            updatesSailPointCertSelectedUsers([...value] as string[], 'remove')
            selectAll(false)
            setValue([])
        }
    }

    useEffect(() => {
        if (isMounted.current) {
            selectAll(value.some(Boolean) && value.length === members.length - members.filter((member) => member.isDisabled).length)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value])

    // Main checkbox that checks / unchecks all the groups
    useEffect(() => {
        if (isMounted.current) {
            if (checkAll && !allSelected) {
                setValue([...members.filter((member) => !member.isDisabled).map((member) => member.entityId)])
                updatesSailPointCertSelectedUsers(
                    [...members.filter((member) => !member.isDisabled).map((member) => member.entityId)],
                    'add',
                )
            }
            if (!checkAll && value.length) {
                updatesSailPointCertSelectedUsers([...value] as string[], 'remove')
                setValue([])
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [checkAll])

    useEffect(() => {
        isMounted.current = true
    }, [])

    const handleCheckboxChange = (e: FormEvent<HTMLInputElement>) => {
        const isChecked = e.currentTarget.checked
        const _value = e.currentTarget.value

        // TODO - double check the logic due to API changes for radio group
        if (isChecked) {
            updatesSailPointCertSelectedUsers([_value], 'add')
        } else {
            updatesSailPointCertSelectedUsers([_value], 'remove')
        }

        toggleValue(_value)
    }

    const largeDescription = description.length > 85

    return (
        <Box zIndex="1" mr="24px">
            <Flex flexDir="row" pb="16px" flexGrow="1">
                <Flex flexDir="column" alignItems="center" pt="4px" pl="2px" w="64px">
                    <Checkbox
                        defaultChecked={allSelected}
                        onCheckedChange={onChange}
                        checked={isIndeterminate ? 'indeterminate' : allSelected}
                        disabled={members.length === 1 && members[0].isDisabled}
                    />
                    <Text fontSize="12px" pt="8px" color="var(--chakra-colors-gray-500)">
                        {typeof value === 'string' ? 1 : value.length}/{members.length}
                    </Text>
                </Flex>
                <AccordionRoot multiple defaultValue={['description']} px={0} width="calc(100% - 64px)">
                    <AccordionItem border="none" px={0} width="calc(100%)" value={description}>
                        <AccordionItemTrigger
                            px="0"
                            py="0"
                            _hover={{ backgroundColor: 'none' }}
                            alignItems="flex-start"
                            maxWidth={largeDescription ? 'calc(100% - 28px)' : 'calc(100%)'}
                        >
                            <Flex flexDir="row" px="12px">
                                <Icon path={mdiAccountGroup} size={1} color="var(--chakra-colors-gray-500)" />
                            </Flex>
                            <Flex flexDir="row" justifyContent="space-between" width="calc(100% - 48px)">
                                <Flex flexDir="column" alignItems="start" width="calc(100% - 20px)">
                                    <Text fontSize="14px" fontWeight="bold" wordBreak="break-word" w="calc(100%)" textAlign="left">
                                        {title}
                                    </Text>
                                    <Flex alignItems="center" pt="6px" width="calc(100%)">
                                        <Text
                                            color={useColorModeValue('rgba(84, 79, 64, 0.7)', 'rgba(255,255,255,0.7)')}
                                            fontSize="12px"
                                            wordBreak="break-word"
                                            textOverflow="ellipsis"
                                            whiteSpace="nowrap"
                                            overflow="hidden"
                                            w={largeDescription ? 'calc(100%)' : 'calc(100% - 28px)'}
                                            textAlign="left"
                                        >
                                            {description}
                                        </Text>
                                        {description.length > 85 && (
                                            <Tooltip
                                                content={description}
                                                css={{
                                                    marginLeft: '8px',
                                                }}
                                            >
                                                <Icon path={mdiInformationOutline} size="16px" color="var(--chakra-colors-gray-500)" />
                                            </Tooltip>
                                        )}
                                    </Flex>
                                </Flex>
                            </Flex>
                        </AccordionItemTrigger>
                        <AccordionItemContent pb={0} px={0} pt="18px">
                            <Flex flexDir="row">
                                <Flex flexDir="column" flexGrow="1">
                                    {members.map((item, i) => (
                                        <Flex flexDir="row" key={i} mb="12px" alignItems="flex-start">
                                            <Flex width="48px" justifyContent="center">
                                                <Checkbox
                                                    {...getItemProps({ value: item.entityId })}
                                                    inputProps={{ onChange: handleCheckboxChange }}
                                                    disabled={item.isDisabled}
                                                />
                                            </Flex>
                                            <Box width="45%" textAlign="left">
                                                <Text
                                                    fontSize="14px"
                                                    fontWeight="bold"
                                                    style={{ overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}
                                                    pr="12px"
                                                    color={item.isDisabled ? disabledColor : ''}
                                                >
                                                    {item.user}
                                                </Text>
                                                {item.metadata?.agreed === 'yes' && (
                                                    <Text fontSize="12px" color={item.isDisabled ? disabledColor : 'status.success'}>
                                                        {t('tasksApprovals.sailPointCertDrawer.body.requestedApproval')}
                                                    </Text>
                                                )}
                                                {item.metadata?.agreed === 'no' && (
                                                    <Text fontSize="12px" color={item.isDisabled ? disabledColor : 'status.error'}>
                                                        {t('tasksApprovals.sailPointCertDrawer.body.requestedRevocation')}
                                                    </Text>
                                                )}
                                            </Box>
                                            <Box width="45%" textAlign="left">
                                                <Text
                                                    fontSize="14px"
                                                    fontWeight="bold"
                                                    style={{ overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}
                                                    color={item.isDisabled ? disabledColor : ''}
                                                >
                                                    {item.manager}
                                                </Text>
                                            </Box>
                                        </Flex>
                                    ))}
                                </Flex>
                            </Flex>
                        </AccordionItemContent>
                    </AccordionItem>
                </AccordionRoot>
            </Flex>
        </Box>
    )
}

export default SailpointCertGroup
